import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchManagementProfilesV2 } from '../../../../store/dashboard/overview/overview_apiHelper';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { setExtendedPollingModules } from '../../../../store/investor/action';
import { TAB_LOCK_IDS_AFTER_DIRECTORS_OVERVIEW, TAB_TO_TASK_TRACKER_MAP, TabModule } from '../../../../utils/constants/commonConstants';
import { TAB_LOCK_COMPLETED_STATUSES, TASK_TRACKER_PENDING_STATUSES, TASK_TRACKER_STATUS } from '../RelatedParties/relatedParty_constants';
import { useBoundedPoll } from '../../../../common/hooks/useBoundedPoll';
import { ColumnLockKey, KARZA_DIN_STEP, PROBE_MERGE_WAIT_MS } from './ManagementProfileTabConstants';

const isTabLockedForColumn = (
    tabId: string,
    tabLockStatus: Record<string, { isLocked?: boolean }> | null,
    taskTrackerMap: Record<string, { status?: string }> | null,
    secondaryModuleList: string[],
): boolean => {
    if (Array.isArray(secondaryModuleList) && !secondaryModuleList.includes(tabId)) {
        const polusEntry = tabLockStatus?.[tabId];
        if (polusEntry == null) return TAB_LOCK_IDS_AFTER_DIRECTORS_OVERVIEW.includes(tabId);
        return !!polusEntry.isLocked;
    }

    const taskTrackerHasEntries = !!taskTrackerMap && Object.keys(taskTrackerMap).length > 0;
    const anyStepCompleteOrFailed =
        taskTrackerHasEntries &&
        (TAB_TO_TASK_TRACKER_MAP[tabId] || []).some((step) => TAB_LOCK_COMPLETED_STATUSES.has(taskTrackerMap?.[step]?.status ?? ''));

    return !anyStepCompleteOrFailed || !!tabLockStatus?.[tabId]?.isLocked;
};

export interface DirectorTableRow {
    // table columns
    name: string;
    din: string;
    imageUrl?: string;
    linkedinUrl?: string;
    followersCount?: string;
    designation?: string;
    tenureBeginDate?: number;
    tenureEndDate?: number;
    isActive?: boolean;
    email?: string;
    phone?: string;
    riskIndicatorCount?: number;
    amlPepRiskCount?: number;
    legalCasesCount?: number;
    creditBureau?: string;
    managementProfileSummary?: string;
    // director detail fields (from UW_FETCH_DIRECTOR_OVERVIEW in the aggregated endpoint)
    dob?: string;
    age?: string;
    gender?: string;
    pan?: string;
    address?: string;
    directorSince?: string;
    directorId?: string;
    creditScore?: number;
    subTitle?: string;
    currentEntities?: any[];
    formerEntities?: any[];
    workExperience?: any[];
    education?: any[];
    // task tracker
    litigationProcessed?: boolean;
    loading: boolean;
}

function useManagementProfileTabLogic() {
    const params = useParams();
    const dispatch = useAppDispatch();

    const [overviewLoading, setOverviewLoading] = useState(true);
    const [awaitingProbeMerge, setAwaitingProbeMerge] = useState(false);
    const [karzaStatusFromFetch, setKarzaStatusFromFetch] = useState<string | undefined>(undefined);
    const [ingestPollExhausted, setIngestPollExhausted] = useState(false);
    const [directors, setDirectors] = useState<DirectorTableRow[]>([]);
    const [selectedDirector, setSelectedDirector] = useState<DirectorTableRow | null>(null);

    const tabLockStatus = useAppSelector((state) => state.investor.tabLockStatus as Record<string, { isLocked?: boolean }> | null);
    const taskTrackerMap = useAppSelector(
        (state) => state.investor.taskTrackerMap as Record<string, { status?: string; dateCreated?: number }> | null,
    );
    const extendedPollingModules = useAppSelector((state) => (state.investor as any).extendedPollingModules ?? []) as string[];
    const secondaryModuleList = useAppSelector(
        (state) => (state.investor.config as any)?.tabs?.filter((t: any) => t.category === 'secondary').map((t: any) => t.id) ?? [],
    );

    const columnLocks: Record<ColumnLockKey, boolean> = {
        riskAlerts: isTabLockedForColumn(TabModule.RELATED_PARTY, tabLockStatus, taskTrackerMap, secondaryModuleList),
        regulatoryChecks: isTabLockedForColumn(TabModule.LEGAL_CHECKS, tabLockStatus, taskTrackerMap, secondaryModuleList),
        legalCases: isTabLockedForColumn(TabModule.LEGAL_CHECKS, tabLockStatus, taskTrackerMap, secondaryModuleList),
    };

    const loadDirectors = useCallback(
        (orgId: string) =>
            fetchManagementProfilesV2({ orgId })
                .then((res: any) => {
                    const profiles = res?.managementProfile?.profiles || [];
                    const taskTrackerData = res?.taskTrackerData || {};
                    setKarzaStatusFromFetch(taskTrackerData[KARZA_DIN_STEP]);
                    const litigationStep = TAB_TO_TASK_TRACKER_MAP.legalChecks[0];
                    const litigationProcessed =
                        litigationStep in taskTrackerData && taskTrackerData[litigationStep] !== TASK_TRACKER_STATUS.TODO;

                    // @TODO move this sort to BE (active first, then lexicographic by name)
                    const rows: DirectorTableRow[] = [...profiles]
                        .sort((a: any, b: any) => {
                            const aActive = a?.isActive ? 0 : 1;
                            const bActive = b?.isActive ? 0 : 1;
                            if (aActive !== bActive) return aActive - bActive;
                            return (a?.name ?? '').localeCompare(b?.name ?? '', undefined, { sensitivity: 'base' });
                        })
                        .map((p: any) => ({
                            name: p.name,
                            din: p.din,
                            imageUrl: p.imageUrl,
                            linkedinUrl: p.linkedinUrl,
                            followersCount: p.followersCount,
                            designation: p.designation,
                            tenureBeginDate: p.tenureBeginDate,
                            tenureEndDate: p.tenureEndDate,
                            isActive: p.isActive,
                            email: p.email,
                            phone: p.phone,
                            riskIndicatorCount: p.riskIndicatorCount,
                            amlPepRiskCount: p.amlPepRiskCount,
                            legalCasesCount: p.legalCasesCount,
                            creditBureau: p.creditBureau,
                            managementProfileSummary: p.managementProfileSummary,
                            dob: p.dob,
                            age: p.age,
                            gender: p.gender,
                            pan: p.pan,
                            address: p.address,
                            directorSince: p.directorSince,
                            directorId: p.directorId,
                            creditScore: p.creditScore,
                            subTitle: p.subTitle,
                            currentEntities: p.currentEntities,
                            formerEntities: p.formerEntities,
                            workExperience: p.workExperience,
                            education: p.education,
                            litigationProcessed,
                            loading: false,
                        }));
                    setDirectors(rows);
                })
                .catch(() => {}),
        [],
    );

    useEffect(() => {
        if (!params.id) return;
        setOverviewLoading(true);
        setKarzaStatusFromFetch(undefined);
        setIngestPollExhausted(false);
        loadDirectors(params.id).finally(() => setOverviewLoading(false));
    }, [params.id, loadDirectors]);

    /*
       The MP response carries the step status inline, so it keeps updating while this page
       self-polls; the redux map only moves while TabSelector polls (capped at 2 min), so it
       is the fallback for the window before the first response lands (and for a response
       that omits the step).
    */
    const reduxKarzaStep = taskTrackerMap?.[KARZA_DIN_STEP];
    const karzaStatus = karzaStatusFromFetch ?? reduxKarzaStep?.status;

    // Ingest is still running on the BE (dispatched, not yet COMPLETE/FAILED): the tab is
    // unlocked but has no rows yet, so the page loads rather than reporting "no data".
    // Dropped once the poll ceiling is hit so an orphaned step can't spin forever.
    const dinIngestInFlight = TASK_TRACKER_PENDING_STATUSES.includes(karzaStatus ?? '') && !ingestPollExhausted;

    const refetchDirectors = useCallback(() => {
        if (params.id) loadDirectors(params.id);
    }, [params.id, loadDirectors]);

    const handleIngestPollCapExhausted = useCallback(() => setIngestPollExhausted(true), []);

    // MP-local poll so ingest landing after TabSelector's polling window still renders.
    // Held back until the mount fetch settles to avoid a duplicate call.
    useBoundedPoll(refetchDirectors, dinIngestInFlight && !overviewLoading, handleIngestPollCapExhausted);

    // Cleanup below runs a commit later than the effect that armed the hold, so the module list
    // it filters must be the current one — the effect closure's copy clobbers concurrent additions.
    const extendedPollingModulesRef = useRef(extendedPollingModules);
    useEffect(() => {
        extendedPollingModulesRef.current = extendedPollingModules;
    }, [extendedPollingModules]);

    const previousKarzaStatusRef = useRef<string | undefined>(karzaStatus);
    /* 
       TODO(BE): Karza marks AION_KARZA_DIN_DETAILS COMPLETE before Probe is done merging,
       so the loader closes while rows are still mutating. Real fix: emit step-COMPLETE
       from AION's triggerFinalLoad, not KarzaServiceImpl. Until then we just hold the
       loader (MP page + sidebar + drawer) for PROBE_MERGE_WAIT_MS and refetch once.
    */
    useEffect(() => {
        const previous = previousKarzaStatusRef.current;
        previousKarzaStatusRef.current = karzaStatus;

        if (karzaStatus !== TASK_TRACKER_STATUS.COMPLETE) return;
        if (previous === undefined || previous === TASK_TRACKER_STATUS.COMPLETE) return;
        if (!params.id) return;

        setAwaitingProbeMerge(true);
        const mpModule = TabModule.MANAGEMENT_PROFILE;
        if (!extendedPollingModules.includes(mpModule)) {
            dispatch(setExtendedPollingModules([...extendedPollingModules, mpModule]));
        }

        const clearExtendedPolling = () => {
            dispatch(setExtendedPollingModules(extendedPollingModulesRef.current.filter((m) => m !== mpModule)));
        };

        const timer = setTimeout(() => {
            const id = params.id;
            if (!id) {
                setAwaitingProbeMerge(false);
                clearExtendedPolling();
                return;
            }
            loadDirectors(id).finally(() => {
                setAwaitingProbeMerge(false);
                clearExtendedPolling();
            });
        }, PROBE_MERGE_WAIT_MS);

        return () => {
            clearTimeout(timer);
            // The timer is the only other release path, so a re-run that kills it mid-hold must
            // drop the hold here or the loader never closes; a later COMPLETE transition re-arms.
            setAwaitingProbeMerge(false);
            clearExtendedPolling();
        };
    }, [karzaStatus, params.id, loadDirectors, dispatch]);

    return {
        overviewLoading: overviewLoading || awaitingProbeMerge || dinIngestInFlight,
        directors,
        columnLocks,
        selectedDirector,
        setSelectedDirector,
        // The date and the status it is claimed for must come from the same row: the redux map is
        // global, so gating the fetch-preferential karzaStatus against it would pair an org-fresh
        // COMPLETE with the previously-viewed org's date.
        lastUpdatedAt: !overviewLoading && reduxKarzaStep?.status === TASK_TRACKER_STATUS.COMPLETE ? reduxKarzaStep.dateCreated : undefined,
    };
}

export default useManagementProfileTabLogic;
